import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useAuth } from '../../hooks/useAuth';
import { readPortalSessionExpired, clearPortalSessionExpired } from 'helpers/portalSession';
import { qaCrmPasswordFor, qaOtpFor } from 'helpers/qaLoginAutofill';
import { brand, colors, palette, typography } from 'theme/tokens';
// The Zenvoya wordmark, bundled with this app. It used to be hotlinked from
// app-dev's build output (a content-hashed file), which broke whenever app-dev
// was redeployed or unreachable. Its colour is `currentColor`, set from the
// theme's primary below.

/* ── Palette — the active brand's login colours (theme/brands) ── */
const V1 = colors.adminLogin.border;  // field borders, disabled fill
const V2 = colors.adminLogin.muted;  // muted text, placeholders
const V3 = colors.adminLogin.primary;  // buttons, headings, focus
const V4 = colors.adminLogin.primaryHover;  // button hover
const V5 = colors.adminLogin.panelBg;  // the left panel
const PANEL_TEXT = colors.adminLogin.panelText;  // muted text on the left panel

const G3 = colors.adminLogin.highlight;  // highlight on the left panel
const G4 = palette.custom.lime[640];  // Bright Chartreuse

const GREY = palette.neutral[21];  // Background Grey
const BLACK = palette.neutral[880]; // Brand Black

const OTP_LENGTH = 6;

/**
 * The six code boxes. This styling, the auto-advance, the paste handling and the
 * `one-time-code` autofill hint are the ORIGINAL screen's, kept as they were —
 * what was wrong with that screen was never its interaction design, it was that
 * `handleVerifyOtp` checked `code.length` and then signed the user in. The code is
 * now issued, stored and verified server-side; the UI is the one that was here.
 */
const FONT = "'Satoshi', sans-serif";
const FONT_BODY = "'Inter', sans-serif";

const otpBoxSx = {
  width: { xs: 38, sm: 50 }, height: 58,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: palette.white,
    height: '100%',
    transition: 'all 0.2s ease',
    '& fieldset': { borderColor: V1 },
    '&:hover fieldset': { borderColor: V2 },
    '&.Mui-focused fieldset': { borderColor: V3, borderWidth: '2px' },
    '&.Mui-focused': { backgroundColor: palette.white, boxShadow: `0 0 0 3px ${colors.adminLogin.focusRing}` },
  },
  '& .MuiInputBase-input': {
    fontSize: typography.fontSize[20.8], fontWeight: typography.fontWeight.bold, textAlign: 'center',
    p: 0, color: BLACK, fontFamily: FONT,
  },
};

const ADMIN_CAPABILITIES = [
  { icon: PeopleAltOutlinedIcon, label: 'Partner Management' },
  { icon: TrendingUpOutlinedIcon, label: 'Revenue & Analytics' },
  { icon: AdminPanelSettingsOutlinedIcon, label: 'Access Control' },
  { icon: SettingsOutlinedIcon, label: 'Platform Config' },
];

const floatingKeyframes = {
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0px) scale(1)' },
    '50%': { transform: 'translateY(-18px) scale(1.03)' },
  },
  '@keyframes floatSlow': {
    '0%, 100%': { transform: 'translateY(0px)' },
    '50%': { transform: 'translateY(-12px)' },
  },
};

const inputSx = {
  mb: 2.5,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: palette.white,
    fontFamily: FONT_BODY,
    transition: 'all 0.2s ease',
    '& fieldset': { borderColor: V1 },
    '&:hover fieldset': { borderColor: V2 },
    '&.Mui-focused fieldset': { borderColor: V3, borderWidth: '2px' },
    '&.Mui-focused': { backgroundColor: palette.white, boxShadow: `0 0 0 3px ${colors.adminLogin.focusRing}` },
  },
  '& .MuiInputBase-input': { fontSize: typography.fontSize[14.4], py: 1.4, px: 1.75, fontFamily: FONT_BODY },
  '& .MuiInputBase-input::placeholder': { color: V2, opacity: 0.6 },
};

// HELLO MOTO
export default function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyOtp, resendOtp, cancelMfa } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // MFA step two. `otpSentTo` is also which step is showing — at that point there
  // is no session, only a pre-auth token held in the auth context.
  const [otpSentTo, setOtpSentTo] = useState('');
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const otpRefs = useRef([]);
  const [resending, setResending] = useState(false);
  const [resendNotice, setResendNotice] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const resendTimer = useRef(null);
  // SMB-4352: why the last session ended, when the transport ended it (refresh
  // refused, account revoked). Read in the initializer and cleared in an effect,
  // not consumed in one go, so StrictMode's double initializer cannot drop it.
  const [sessionEndedNotice] = useState(() => readPortalSessionExpired('crm'));
  useEffect(() => clearPortalSessionExpired('crm'), []);
  // QA autofill (QA_LOGIN_AUTOFILL in flagSwitch/SwitchConfig): the QA admin email
  // fills the password. Same shape as the partner screen's effect on its code.
  useEffect(() => {
    const qaPassword = qaCrmPasswordFor(email);
    if (qaPassword) setPassword(qaPassword);
  }, [email]);

  /** 30-second lockout after a resend. Cleared on unmount so it cannot outlive the screen. */
  const startResendCooldown = () => {
    setResendIn(30);
    clearInterval(resendTimer.current);
    resendTimer.current = setInterval(() => {
      setResendIn((n) => {
        if (n <= 1) {
          clearInterval(resendTimer.current);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(resendTimer.current), []);

  /* ── The original screen's code-entry behaviour, unchanged ────────────────── */

  /**
   * Accepts one digit per box AND a whole code arriving in one.
   *
   * The original took `e.target.value.slice(-1)`, which is right for typing and
   * wrong for everything else: `autoComplete="one-time-code"` means iOS and Safari
   * fill the FIRST box with all six digits, and slicing kept only the last one, so
   * autofill silently produced a one-digit code. Fast typing lost digits the same
   * way — each keystroke arriving before the focus move landed on the box that had
   * just been filled.
   */
  const handleOtpChange = useCallback((index, raw) => {
    const digits = String(raw ?? '').replace(/\D/g, '');

    if (!digits) {
      setOtp((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }

    setOtp((prev) => {
      const next = [...prev];
      digits.split('').forEach((ch, offset) => {
        const slot = index + offset;
        if (slot < OTP_LENGTH) next[slot] = ch;
      });
      return next;
    });

    const landed = Math.min(index + digits.length, OTP_LENGTH - 1);
    otpRefs.current[landed]?.focus();
  }, []);

  const handleOtpKeyDown = useCallback(
    (index, e) => {
      if (e.key === 'Backspace' && !otp[index] && index > 0) {
        otpRefs.current[index - 1]?.focus();
      }
      // Enter on the last box (or any box, once all six are filled) verifies —
      // same reason as the password field: do not depend on implicit submission.
      if (e.key === 'Enter' && !e.nativeEvent?.isComposing) {
        e.preventDefault();
        if (otp.every((d) => d !== '')) handleVerifyOtp(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [otp],
  );

  const handleOtpPaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setOtp(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[focusIdx]?.focus();
  }, []);

  const otpComplete = otp.every((d) => d !== '');

  /**
   * First login: the account is on a generated password and must rotate it before
   * doing anything else. Shared by both paths below so the redirect cannot end up
   * applying only when MFA happens to be off.
   */
  const afterSession = (result) =>
    navigate(result.mustChangePassword ? '/admin/change-password' : '/admin', { replace: true });

  /**
   * Sign in against POST /app/v1/crm/auth/login.
   *
   * Two steps when ENABLE_PORTAL_MFA is on: credentials, then a code that the
   * SERVER issued, stored and verifies. The screen this replaced also had two
   * steps, but its 6-digit field only checked the code's LENGTH — nothing issued
   * or verified it — so it implied a second factor that did not exist. That is
   * why it was deleted rather than re-pointed: a convincing but inert 2FA screen
   * invites trusting an account more than its credentials warrant.
   */
  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    // Leading/trailing spaces are dropped; spaces inside the password are kept.
    const trimmedPassword = password.trim();
    if (!trimmedPassword) {
      setError('Please enter your password');
      return;
    }
    setBusy(true);
    try {
      const result = await login(email, trimmedPassword);
      if (!result.ok) {
        // The server's wording is its only failure discriminator, so show it
        // verbatim — several distinct causes deliberately share one message.
        setError(result.message);
        return;
      }

      // Credentials accepted, factor outstanding. No session exists yet.
      if (result.mfaRequired) {
        const sentTo = result.email || email;
        setOtpSentTo(sentTo);
        setOtp(qaOtpFor('crm', sentTo) || Array(OTP_LENGTH).fill(''));
        setTimeout(() => { otpRefs.current[0]?.focus(); }, 100);
        return;
      }

      afterSession(result);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Enter submits, explicitly.
   *
   * The markup is already a single <form> with one type="submit" button inside it,
   * which is all native implicit submission needs — but it was reported as not
   * working, and implicit submission is easy to lose to things outside this file
   * (a wrapper that swallows keydown, an extension, an IME composition). Handling
   * the key directly costs nothing and does not depend on that machinery.
   *
   * `isComposing` is checked because Enter commits a candidate in Japanese/Chinese
   * input methods; submitting on that keystroke would fire mid-word.
   */
  const submitOnEnter = (e) => {
    if (e.key !== 'Enter' || e.nativeEvent?.isComposing) return;
    e.preventDefault();
    if (!busy) handleSignIn(e);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < OTP_LENGTH) {
      setError('Please enter the complete 6-digit code.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const result = await verifyOtp(code);
      if (!result.ok) {
        setError(result.message);
        // Clear the boxes on a rejection: retyping over six half-filled inputs is
        // worse than starting again, and the code is single-use either way.
        setOtp(Array(OTP_LENGTH).fill(''));
        otpRefs.current[0]?.focus();
        return;
      }
      afterSession(result);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Resend, with something to show for it.
   *
   * The call always worked; it just said nothing on success, so the only visible
   * effect was the boxes clearing — indistinguishable from a dead link. It now
   * reports sending, confirms, and then refuses for 30 seconds.
   *
   * The cooldown is not politeness: each resend REPLACES the stored code, so
   * clicking twice invalidates the code from the first click — which someone
   * mid-typing then submits and is told is invalid.
   */
  const handleResendOtp = async (e) => {
    e?.preventDefault?.();
    if (resending || resendIn > 0) return;

    setError('');
    setResendNotice('');
    setResending(true);
    try {
      const result = await resendOtp();
      if (!result.ok) {
        setError(result.message);
        return;
      }
      // The QA account's code survives a resend (the backend keeps it fixed).
      setOtp(qaOtpFor('crm', otpSentTo) || Array(OTP_LENGTH).fill(''));
      setResendNotice(`New code sent to ${otpSentTo}.`);
      otpRefs.current[0]?.focus();
      startResendCooldown();
    } finally {
      setResending(false);
    }
  };

  const handleBackToPassword = () => {
    cancelMfa();
    setOtpSentTo('');
    setOtp(Array(OTP_LENGTH).fill(''));
    setError('');
  };


  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', fontFamily: FONT, ...floatingKeyframes }}>

      {/* ===== LEFT PANEL ===== */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '48%',
        backgroundColor: V5,
        px: 7, py: 5,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Ambient orbs */}
        <Box sx={{
          position: 'absolute', top: '-10%', right: '-10%',
          width: 450, height: 450, borderRadius: '50%',
          backgroundColor: colors.adminLogin.orb1,
          animation: 'float 10s ease-in-out infinite',
          filter: 'blur(60px)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '-15%', left: '-8%',
          width: 380, height: 380, borderRadius: '50%',
          backgroundColor: colors.adminLogin.orb2,
          animation: 'floatSlow 12s ease-in-out infinite',
          filter: 'blur(50px)',
        }} />
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          width: 180, height: 180, borderRadius: '50%',
          backgroundColor: colors.adminLogin.orb3,
          filter: 'blur(40px)',
        }} />

        {/* Grid overlay */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.025, pointerEvents: 'none',
          backgroundImage: `repeating-linear-gradient(0deg, ${palette.alpha.white[8]} 0px, ${palette.alpha.white[8]} 1px, transparent 1px, transparent 56px), repeating-linear-gradient(90deg, ${palette.alpha.white[8]} 0px, ${palette.alpha.white[8]} 1px, transparent 1px, transparent 56px)`,
        }} />

        {/* Logo */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, position: 'relative', zIndex: 1 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: 2, py: 1, borderRadius: '12px',
            backgroundColor: palette.alpha.white[95],
            alignSelf: 'flex-start',
          }}>
            <Box component={brand.Wordmark} role="img" aria-label={brand.name} sx={{ height: brand.adminLoginLogoHeight, width: 'auto', display: 'block', color: 'primary.main' }} />
          </Box>
          <Typography sx={{ color: G3, fontSize: typography.fontSize[11.52], fontWeight: typography.fontWeight.bold, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            Admin & CRM Portal
          </Typography>
        </Box>

        {/* Center content */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography sx={{
            color: G3, fontSize: typography.fontSize[12.8], fontWeight: typography.fontWeight.bold,
            letterSpacing: '0.2em', textTransform: 'uppercase', mb: 2,
          }}>
            Journey Beyond Ordinary
          </Typography>

          <Typography sx={{
            color: colors.adminLogin.panelHeading, fontWeight: typography.fontWeight.extraBold, fontSize: typography.fontSize[40], lineHeight: typography.lineHeight.none,
            letterSpacing: '-0.03em', fontFamily: FONT, mb: 2,
          }}>
            Command Center for<br />Travel Commerce
          </Typography>

          <Typography sx={{
            color: PANEL_TEXT, fontSize: typography.fontSize[17.6], lineHeight: typography.lineHeight.relaxed, letterSpacing: '-0.03em',
            fontFamily: FONT_BODY, maxWidth: 400, mb: 5, opacity: 0.7,
          }}>
            Manage partners, configure experiences, and drive revenue across the entire Zenvoya ecosystem.
          </Typography>

          {/* Capability cards — 2x2 */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            {ADMIN_CAPABILITIES.map((cap) => (
              <Box key={cap.label} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.8, borderRadius: '12px',
                background: colors.adminLogin.cardBg,
                border: `1px solid ${colors.adminLogin.cardBorder}`,
                backdropFilter: 'blur(12px)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: `${colors.adminLogin.cardHoverBg}`,
                  border: `1px solid ${colors.adminLogin.cardHoverBorder}`,
                  transform: 'translateY(-2px)',
                },
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '10px',
                  backgroundColor: colors.adminLogin.iconBoxBg,
                  border: `1px solid ${colors.adminLogin.iconBoxBorder}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <cap.icon sx={{ color: G3, fontSize: typography.fontSize[20] }} />
                </Box>
                <Typography sx={{
                  color: colors.adminLogin.cardText, fontSize: typography.fontSize[14.72], fontWeight: typography.fontWeight.semiBold,
                  fontFamily: FONT, lineHeight: typography.lineHeight.snug,
                }}>
                  {cap.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Authorized badge */}
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1, mt: 4,
            px: 2, py: 0.8, borderRadius: '20px',
            backgroundColor: colors.adminLogin.cardBg,
            border: `1px solid ${colors.adminLogin.badgeBorder}`,
          }}>
            <LockOutlinedIcon sx={{ color: PANEL_TEXT, fontSize: typography.fontSize[15], opacity: 0.6 }} />
            <Typography sx={{ color: PANEL_TEXT, fontSize: typography.fontSize[12.8], fontWeight: typography.fontWeight.semiBold, letterSpacing: '0.06em', opacity: 0.5 }}>
              Authorized Personnel Only
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Typography sx={{ color: PANEL_TEXT, fontSize: typography.fontSize[13.6], position: 'relative', zIndex: 1, fontFamily: FONT_BODY, opacity: 0.25 }}>
          &copy; {new Date().getFullYear()} Zenvoya Inc. All rights reserved.
        </Typography>
      </Box>

      {/* ===== ACCENT DIVIDER ===== */}
      <Box sx={{
        width: 2,
        backgroundColor: colors.adminLogin.divider,
      }} />

      {/* ===== RIGHT PANEL ===== */}
      <Box sx={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        px: { xs: 3, sm: 6 }, py: 4,
        backgroundColor: GREY,
        position: 'relative',
      }}>
        <Box sx={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box component={brand.Wordmark} role="img" aria-label={brand.name} sx={{ height: brand.adminLoginLogoHeight, width: 'auto', display: 'block', color: 'primary.main' }} />
          </Box>

          {/* ── Credentials ── */}
          <>
              {/* Header — hidden on the OTP step, which has its own Verification header below "Back to login" */}
              {!otpSentTo && (
                <>
                  <Typography sx={{ color: V3, fontSize: typography.fontSize[11.52], fontWeight: typography.fontWeight.bold, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1 }}>
                    Welcome back
                  </Typography>
                  <Typography sx={{ color: BLACK, fontWeight: typography.fontWeight.extraBold, fontSize: typography.fontSize[29.6], mb: 0.5, letterSpacing: '-0.03em', lineHeight: typography.lineHeight.none, fontFamily: FONT }}>
                    Sign In
                  </Typography>
                  <Typography sx={{ color: V2, fontSize: typography.fontSize[14], mb: 4, lineHeight: typography.lineHeight.relaxed, letterSpacing: '-0.03em', fontFamily: FONT_BODY, opacity: 0.7 }}>
                    Access your admin dashboard to manage operations.
                  </Typography>
                </>
              )}

              {error && (
                <Box sx={{ mb: 3, p: 1.5, borderRadius: '10px', backgroundColor: palette.tw.red[50], border: `1px solid ${palette.tw.red[200]}` }}>
                  <Typography sx={{ color: palette.custom.red[460], fontSize: typography.fontSize[13.12], fontWeight: typography.fontWeight.medium, fontFamily: FONT_BODY }}>{error}</Typography>
                </Box>
              )}
              {/* SMB-4352 — the session was ended for them, not by them. Gives
                  way to a real error and to the OTP step. */}
              {sessionEndedNotice && !error && !otpSentTo && (
                <Box role="status" sx={{ mb: 3, p: 1.5, borderRadius: '10px', backgroundColor: palette.tw.amber[50], border: `1px solid ${palette.tw.amber[200]}` }}>
                  <Typography sx={{ color: palette.tw.amber[800], fontSize: typography.fontSize[13.12], fontWeight: typography.fontWeight.medium, fontFamily: FONT_BODY }}>{sessionEndedNotice}</Typography>
                </Box>
              )}

              {/*
                STEP TWO — the emailed code. Replaces the credential fields rather
                than sitting below them: the password step is done, and leaving it
                submittable would issue a second code and invalidate the one the
                user is holding.
              */}
              {otpSentTo ? (
                <>
                  <Button
                    startIcon={<ArrowBackIcon sx={{ fontSize: typography.fontSize[18] }} />}
                    onClick={handleBackToPassword}
                    sx={{
                      color: V3, textTransform: 'none', fontWeight: typography.fontWeight.semiBold, fontFamily: FONT,
                      fontSize: typography.fontSize[13.12], mb: 3, ml: -1, px: 1,
                      '&:hover': { backgroundColor: `${colors.adminLogin.ghostHover}` },
                    }}
                  >
                    Back to login
                  </Button>

                  <Typography sx={{ color: V3, fontSize: typography.fontSize[11.52], fontWeight: typography.fontWeight.bold, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1 }}>
                    Verification
                  </Typography>
                  <Typography sx={{ color: BLACK, fontWeight: typography.fontWeight.extraBold, fontSize: typography.fontSize[29.6], mb: 0.5, letterSpacing: '-0.03em', lineHeight: typography.lineHeight.none, fontFamily: FONT }}>
                    Enter OTP Code
                  </Typography>
                  <Typography sx={{ color: V2, fontSize: typography.fontSize[14], mb: 1, lineHeight: typography.lineHeight.relaxed, letterSpacing: '-0.03em', fontFamily: FONT_BODY, opacity: 0.7 }}>
                    We've sent a 6-digit verification code to
                  </Typography>
                  <Typography sx={{ color: BLACK, fontSize: typography.fontSize[14], fontWeight: typography.fontWeight.semiBold, mb: 1, fontFamily: FONT }}>
                    {otpSentTo}
                  </Typography>
                  <Typography sx={{ color: V2, fontSize: typography.fontSize[12.48], mb: 4, fontFamily: FONT_BODY, opacity: 0.7 }}>
                    It expires in 10 minutes and can be used once.
                  </Typography>

                  <Box component="form" onSubmit={handleVerifyOtp} noValidate>
                    <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1.5 }, justifyContent: 'center', mb: 4 }}>
                      {otp.map((digit, idx) => (
                        <TextField
                          key={idx}
                          value={digit}
                          onChange={(e) => handleOtpChange(idx, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                          onPaste={idx === 0 ? handleOtpPaste : undefined}
                          inputRef={(el) => { otpRefs.current[idx] = el; }}
                          /*
                            maxLength is the FULL code length, not 1: a cap of one
                            makes the browser drop anything beyond it before React
                            sees it, which ate digits when keystrokes outpaced the
                            focus move and reduced a six-digit autofill to one.
                            handleOtpChange distributes; each box still shows one
                            digit from state.
                          */
                          inputProps={{ maxLength: OTP_LENGTH, inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'one-time-code' }}
                          size="small"
                          sx={otpBoxSx}
                        />
                      ))}
                    </Box>

                    {resendNotice && !error && (
                      <Box sx={{ mb: 2, p: 1.25, borderRadius: '10px', backgroundColor: palette.tw.green[50], border: `1px solid ${palette.tw.green[200]}` }}>
                        <Typography sx={{ color: palette.custom.green[650], fontSize: typography.fontSize[12.8], fontWeight: typography.fontWeight.medium, textAlign: 'center', fontFamily: FONT_BODY }}>
                          {resendNotice}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                      <Typography component="span" sx={{ color: V2, fontSize: typography.fontSize[13.12], fontFamily: FONT_BODY, opacity: 0.7 }}>
                        Didn't receive the code?{' '}
                      </Typography>
                      {/* Now actually resends — and issuing a new code retires the old one. */}
                      <Link
                        href="#"
                        underline="none"
                        onClick={handleResendOtp}
                        sx={{
                          color: resending || resendIn > 0 ? V2 : V3,
                          fontSize: typography.fontSize[13.12], fontWeight: typography.fontWeight.semiBold, fontFamily: FONT,
                          cursor: resending || resendIn > 0 ? 'not-allowed' : 'pointer',
                          transition: 'color 0.2s',
                          '&:hover': { color: resending || resendIn > 0 ? V2 : V4 },
                        }}
                      >
                        {resending ? 'Sending…' : resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend'}
                      </Link>
                    </Box>

                    <Button type="submit" fullWidth variant="contained" size="large" disabled={!otpComplete || busy} sx={{
                      py: 1.5, fontSize: typography.fontSize[14.4], fontWeight: typography.fontWeight.bold, borderRadius: '10px', fontFamily: FONT,
                      backgroundColor: otpComplete ? V3 : V1,
                      boxShadow: otpComplete ? `0 6px 20px ${colors.adminLogin.buttonShadow}` : 'none',
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: otpComplete ? V4 : V1,
                        boxShadow: otpComplete ? `0 10px 28px ${colors.adminLogin.buttonShadowHover}` : 'none',
                        transform: otpComplete ? 'translateY(-1px)' : 'none',
                      },
                      '&:active': { transform: 'translateY(0px)' },
                      '&.Mui-disabled': { backgroundColor: V1, color: V2 },
                    }}>
                      {busy ? 'Verifying…' : 'Verify & Continue'}
                    </Button>
                  </Box>
                </>
              ) : (
              <Box component="form" onSubmit={handleSignIn} noValidate>
                <Typography sx={{ color: BLACK, fontWeight: typography.fontWeight.semiBold, fontSize: typography.fontSize[13.12], mb: 0.75, fontFamily: FONT }}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth placeholder="you@zenvoya.com" value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                  onKeyDown={submitOnEnter}
                  type="email" size="small" sx={inputSx} autoComplete="email"
                />

                {/*
                  The "Forgot password?" link that sat beside this label is gone.
                  It was inert — href="#" with preventDefault, no reset flow behind
                  it — so it advertised a recovery path the portal does not have.
                  Admin credentials are reissued out of band (see the tenant
                  credential reset route), not self-served. The flex row it needed
                  went with it; the label now matches the Email Address one above.
                */}
                <Typography sx={{ color: BLACK, fontWeight: typography.fontWeight.semiBold, fontSize: typography.fontSize[13.12], mb: 0.75, fontFamily: FONT }}>
                  Password
                </Typography>
                <TextField
                  fullWidth placeholder="Enter your password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={submitOnEnter}
                  type={showPassword ? 'text' : 'password'} size="small"
                  sx={inputSx} autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {/*
                          tabIndex -1: a redundant control (the password is typed
                          either way) that otherwise sits between Password and Sign
                          In, so Tab from the password field hit the eye icon rather
                          than the submit button.
                        */}
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small"
                          tabIndex={-1}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          sx={{ color: V2, '&:hover': { color: V3 } }}>
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/*
                  "Remember me for 30 days" was here. Removed rather than wired up:
                  its state was read by nothing, and the promise is one this session
                  model cannot keep — the portal session lives in sessionStorage and
                  dies with the tab, on a 15-minute access token with a 12h sliding /
                  7d absolute refresh cap (S-204). Thirty days would mean a
                  long-lived credential on disk, which is the opposite of that
                  design.
                */}
                <Box sx={{ mb: 3.5 }} />

                <Button type="submit" fullWidth variant="contained" size="large" disabled={busy} sx={{
                  py: 1.5, fontSize: typography.fontSize[14.4], fontWeight: typography.fontWeight.bold, borderRadius: '10px', fontFamily: FONT,
                  backgroundColor: V3,
                  boxShadow: `0 6px 20px ${colors.adminLogin.buttonShadow}`,
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: V4,
                    boxShadow: `0 10px 28px ${colors.adminLogin.buttonShadowHover}`,
                    transform: 'translateY(-1px)',
                  },
                  '&:active': { transform: 'translateY(0px)' },
                  '&.Mui-disabled': { backgroundColor: V1, color: V2 },
                }}>
                  {/*
                    Sign-in makes a network call and, with MFA on, also sends an
                    email — so it can take a noticeable moment. With no feedback the
                    screen looked inert, which reads as "Enter did nothing" and
                    invites a second submit that would issue a second code and
                    invalidate the first.
                  */}
                  {busy ? (
                    <>
                      <CircularProgress size={18} thickness={5} sx={{ color: V2, mr: 1.25 }} />
                      Signing in…
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </Box>
              )}
          </>

          {/* Security badge */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
            mt: 4, py: 1.5, borderRadius: '10px',
            backgroundColor: `${colors.adminLogin.noteBg}`,
            border: `1px solid ${V1}`,
          }}>
            <Box sx={{
              width: 6, height: 6, borderRadius: '50%',
              backgroundColor: G4,
              boxShadow: `0 0 8px ${palette.alpha.customLime640[50]}`,
            }} />
            <Typography sx={{ color: V2, fontSize: typography.fontSize[11.52], letterSpacing: '0.02em', fontFamily: FONT_BODY, opacity: 0.7 }}>
              Secured internal access
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
